import React from 'react';
import { AdvisoryEvidenceText, AdvisoryEvidenceValue } from './AdvisoryInlineEvidence';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { AnyCompositeSectionV01 } from '../index';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';

export interface SpecializedSectionProps {
  section: AnyCompositeSectionV01;
  compact: boolean;
}

type BusinessModelRow = {
  block: string;
  current_configuration: string;
  fit: string;
  evidence: string;
};

type RoadmapRow = {
  phase: string;
  window: string;
  trigger: string;
  owner: string;
  status: string;
};

type ScenarioRow = {
  scenario: string;
  assumptions: string;
  outcome: string;
  probability: string;
};

type SystemNode = { node: string; role: string };
type SystemEdge = { from: string; to: string; relationship: string };
type ForecastRow = { line: string; period: string; value: string };

const labelSx = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.13em',
  lineHeight: 1.4,
  textTransform: 'uppercase' as const,
};

const monoSx = {
  fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.01em',
};

function EmptyValue() {
  const { t } = useUntypedTranslation();
  return (
    <Typography
      component="span"
      sx={theme => ({ color: theme.colors.inkTertiary, fontStyle: 'italic' })}
    >
      {t('advisory:brief.notProvided')}
    </Typography>
  );
}

function KnownEmpty() {
  const { t } = useUntypedTranslation();
  return (
    <Typography sx={theme => ({ color: theme.colors.inkTertiary, fontStyle: 'italic' })}>
      {t('advisory:brief.knownEmpty')}
    </Typography>
  );
}

function TextValue({ value }: { value: string }) {
  return value.trim() ? (
    <Typography
      sx={theme => ({
        color: theme.colors.inkSecondary,
        fontSize: theme.visualPrimitives.briefBodySize,
        lineHeight: theme.visualPrimitives.briefBodyLineHeight,
        whiteSpace: 'pre-wrap',
      })}
    >
      <AdvisoryEvidenceText>{value}</AdvisoryEvidenceText>
    </Typography>
  ) : (
    <EmptyValue />
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="h4"
      sx={theme => ({ ...labelSx, color: theme.colors.inkTertiary, marginBottom: 1 })}
    >
      {children}
    </Typography>
  );
}

function Insight({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <Box
      sx={theme => ({
        borderLeft: `2px solid ${accent ? theme.colors.lavender : theme.visualPrimitives.border}`,
        paddingLeft: 3,
        minWidth: 0,
      })}
    >
      <FieldLabel>{label}</FieldLabel>
      <TextValue value={value} />
    </Box>
  );
}

export function BusinessModelCanvasRenderer({ section, compact }: SpecializedSectionProps) {
  const data = section.data as {
    rows?: BusinessModelRow[];
    constraining_block?: string;
    why_constraining?: string;
    alternative?: string;
    comparable?: string;
  };
  const rows = data.rows ?? [];
  const fields = section.fields as readonly string[];
  const constrained = (data.constraining_block ?? '').trim().toLowerCase();

  return (
    <Box
      data-testid="advisory-business-model-canvas"
      sx={{ display: 'grid', gap: compact ? 3 : 4 }}
    >
      {rows.length === 0 ? (
        <KnownEmpty />
      ) : (
        <Box
          component="ol"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {rows.map((row, index) => {
            const isConstraint =
              constrained.length > 0 && row.block.trim().toLowerCase() === constrained;
            return (
              <Box
                component="li"
                data-canvas-block={row.block}
                key={index}
                sx={theme => ({
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr)',
                  alignContent: 'start',
                  gap: 2,
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  minHeight: compact ? 180 : 220,
                  padding: compact ? 2.5 : 3,
                  border: `1px solid ${
                    isConstraint ? theme.colors.lavender : theme.visualPrimitives.border
                  }`,
                  borderRadius: 1,
                  backgroundColor: isConstraint
                    ? alpha(theme.colors.lavender, 0.1)
                    : alpha(theme.colors.sand, 0.42),
                })}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                  }}
                >
                  <Typography
                    component="h4"
                    sx={theme => ({
                      color: theme.colors.inkPrimary,
                      fontSize: compact ? 17 : 19,
                      fontWeight: 500,
                      lineHeight: 1.25,
                    })}
                  >
                    <AdvisoryEvidenceValue>{row.block}</AdvisoryEvidenceValue>
                  </Typography>
                  <Typography
                    component="span"
                    sx={theme => ({
                      ...labelSx,
                      color: isConstraint ? theme.colors.lavenderDeep : theme.colors.inkTertiary,
                    })}
                  >
                    <AdvisoryEvidenceValue>{row.fit}</AdvisoryEvidenceValue>
                  </Typography>
                </Box>
                <Box>
                  <FieldLabel>Current configuration</FieldLabel>
                  <TextValue value={row.current_configuration} />
                </Box>
                <Box sx={{ alignSelf: 'end' }}>
                  <FieldLabel>Evidence</FieldLabel>
                  <TextValue value={row.evidence} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {fields.includes('constraining_block') ? (
        <Insight accent label="Constraining block" value={data.constraining_block ?? ''} />
      ) : null}
      {fields.includes('why_constraining') ? (
        <Insight label="Why constraining" value={data.why_constraining ?? ''} />
      ) : null}
      {fields.includes('alternative') ? (
        <Insight label="Alternative" value={data.alternative ?? ''} />
      ) : null}
      {fields.includes('comparable') ? (
        <Insight label="Comparable" value={data.comparable ?? ''} />
      ) : null}
    </Box>
  );
}

export function RoadmapTimelineRenderer({ section, compact }: SpecializedSectionProps) {
  const rows = ((section.data as { rows?: RoadmapRow[] }).rows ?? []) as RoadmapRow[];
  if (rows.length === 0) return <KnownEmpty />;

  return (
    <Box
      component="ol"
      data-testid="advisory-roadmap-timeline"
      sx={{ listStyle: 'none', margin: 0, padding: 0 }}
    >
      {rows.map((row, index) => (
        <Box
          component="li"
          data-roadmap-phase={row.phase}
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '30px minmax(0, 1fr)', sm: '44px minmax(0, 1fr)' },
            gap: { xs: 1.5, sm: 2.5 },
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateRows: '30px 1fr', justifyItems: 'center' }}>
            <Box
              sx={theme => ({
                ...monoSx,
                display: 'grid',
                placeItems: 'center',
                width: 30,
                height: 30,
                border: `1px solid ${theme.colors.lavender}`,
                borderRadius: '50%',
                color: theme.colors.lavenderDeep,
                backgroundColor: theme.palette.background.default,
                fontSize: 11,
                zIndex: 1,
              })}
            >
              {String(index + 1).padStart(2, '0')}
            </Box>
            {index < rows.length - 1 ? (
              <Box
                aria-hidden="true"
                sx={theme => ({
                  width: '1px',
                  height: '100%',
                  backgroundColor: theme.colors.lavender,
                })}
              />
            ) : null}
          </Box>
          <Box
            sx={theme => ({
              paddingBottom: compact ? 3 : 4,
              marginBottom: index < rows.length - 1 ? 1 : 0,
              borderBottom:
                index < rows.length - 1
                  ? `1px solid ${theme.visualPrimitives.borderLight}`
                  : 'none',
              minWidth: 0,
            })}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Typography
                component="h4"
                sx={theme => ({
                  color: theme.colors.inkPrimary,
                  fontSize: compact ? 17 : 20,
                  fontWeight: 500,
                  lineHeight: 1.3,
                })}
              >
                <AdvisoryEvidenceValue>{row.phase}</AdvisoryEvidenceValue>
              </Typography>
              <Typography
                component="span"
                sx={theme => ({ ...labelSx, color: theme.colors.lavenderDeep })}
              >
                <AdvisoryEvidenceValue>{row.status}</AdvisoryEvidenceValue>
              </Typography>
            </Box>
            <Typography
              sx={theme => ({
                ...monoSx,
                color: theme.colors.inkTertiary,
                fontSize: 12,
                marginTop: 1,
              })}
            >
              <AdvisoryEvidenceValue>{row.window}</AdvisoryEvidenceValue>
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(140px, 1fr)' },
                gap: 2.5,
                marginTop: 2,
              }}
            >
              <Box>
                <FieldLabel>Trigger</FieldLabel>
                <TextValue value={row.trigger} />
              </Box>
              <Box>
                <FieldLabel>Owner</FieldLabel>
                <TextValue value={row.owner} />
              </Box>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function ScenarioMatrixRenderer({ section, compact }: SpecializedSectionProps) {
  const rows = ((section.data as { rows?: ScenarioRow[] }).rows ?? []) as ScenarioRow[];
  if (rows.length === 0) return <KnownEmpty />;

  return (
    <Box
      component="ol"
      data-testid="advisory-scenario-matrix"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: 1.5,
        listStyle: 'none',
        margin: 0,
        padding: 0,
      }}
    >
      {rows.map((row, index) => (
        <Box
          component="li"
          data-scenario={row.scenario}
          key={index}
          sx={theme => ({
            display: 'grid',
            gridTemplateRows: 'auto auto 1fr',
            gap: 2.5,
            minHeight: compact ? 210 : 260,
            padding: compact ? 2.5 : 3.5,
            border: `1px solid ${theme.visualPrimitives.border}`,
            borderRadius: 1,
            backgroundColor: index === 0 ? alpha(theme.colors.lavender, 0.08) : 'transparent',
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography
              component="h4"
              sx={theme => ({
                color: theme.colors.inkPrimary,
                fontSize: compact ? 17 : 20,
                fontWeight: 500,
                lineHeight: 1.25,
              })}
            >
              <AdvisoryEvidenceValue>{row.scenario}</AdvisoryEvidenceValue>
            </Typography>
            <Typography
              component="span"
              sx={theme => ({
                ...monoSx,
                flexShrink: 0,
                color: theme.colors.lavenderDeep,
                fontSize: 12,
              })}
            >
              <AdvisoryEvidenceValue>{row.probability}</AdvisoryEvidenceValue>
            </Typography>
          </Box>
          <Box>
            <FieldLabel>Assumptions</FieldLabel>
            <TextValue value={row.assumptions} />
          </Box>
          <Box
            sx={theme => ({
              alignSelf: 'end',
              borderTop: `1px solid ${theme.visualPrimitives.borderLight}`,
              paddingTop: 2,
            })}
          >
            <FieldLabel>Outcome</FieldLabel>
            <TextValue value={row.outcome} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function SystemMapRenderer({ section, compact }: SpecializedSectionProps) {
  const data = section.data as { nodes?: SystemNode[]; edges?: SystemEdge[] };
  const nodes = data.nodes ?? [];
  const edges = data.edges ?? [];
  const fields = section.fields as readonly string[];

  return (
    <Box data-testid="advisory-system-map" sx={{ display: 'grid', gap: compact ? 3 : 4 }}>
      {nodes.length === 0 ? (
        <KnownEmpty />
      ) : (
        <Box
          component="ol"
          aria-label="System actors"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {nodes.map((node, index) => (
            <Box
              component="li"
              data-system-node={node.node}
              key={index}
              sx={theme => ({
                position: 'relative',
                padding: compact ? 2 : 2.5,
                border: `1px solid ${theme.visualPrimitives.border}`,
                borderRadius: 1,
                backgroundColor:
                  index === 0 ? alpha(theme.colors.lavender, 0.1) : alpha(theme.colors.sand, 0.36),
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -3,
                  left: 20,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: theme.colors.lavender,
                },
              })}
            >
              <Typography
                component="h4"
                sx={theme => ({ color: theme.colors.inkPrimary, fontWeight: 500, lineHeight: 1.3 })}
              >
                <AdvisoryEvidenceValue>{node.node}</AdvisoryEvidenceValue>
              </Typography>
              <Typography
                sx={theme => ({
                  color: theme.colors.inkTertiary,
                  fontSize: 13,
                  lineHeight: 1.5,
                  marginTop: 0.75,
                })}
              >
                <AdvisoryEvidenceValue>{node.role}</AdvisoryEvidenceValue>
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {fields.includes('edges') ? (
        <Box>
          <FieldLabel>Relationships</FieldLabel>
          {edges.length === 0 ? (
            <KnownEmpty />
          ) : (
            <Box
              component="ol"
              aria-label="System relationships"
              sx={{ display: 'grid', gap: 1, listStyle: 'none', margin: 0, padding: 0 }}
            >
              {edges.map((edge, index) => (
                <Box
                  component="li"
                  data-system-edge={`${edge.from} → ${edge.to}`}
                  key={index}
                  sx={theme => ({
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr) 24px minmax(0, 1fr)',
                      sm: 'minmax(0, 1fr) minmax(150px, 0.8fr) minmax(0, 1fr)',
                    },
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    paddingY: 1.5,
                    borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
                  })}
                >
                  <Typography sx={{ fontWeight: 500, minWidth: 0 }}>
                    <AdvisoryEvidenceText>{edge.from}</AdvisoryEvidenceText>
                  </Typography>
                  <Box
                    sx={theme => ({
                      position: 'relative',
                      minHeight: 28,
                      display: 'grid',
                      placeItems: 'center',
                      color: theme.colors.inkTertiary,
                      fontSize: 12,
                      textAlign: 'center',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: '50%',
                        height: '1px',
                        backgroundColor: theme.colors.lavender,
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: 'calc(50% - 3px)',
                        width: 6,
                        height: 6,
                        borderTop: `1px solid ${theme.colors.lavender}`,
                        borderRight: `1px solid ${theme.colors.lavender}`,
                        transform: 'rotate(45deg)',
                      },
                    })}
                  >
                    <Typography
                      component="span"
                      sx={theme => ({
                        display: { xs: 'none', sm: 'inline' },
                        position: 'relative',
                        zIndex: 1,
                        paddingX: 1,
                        color: theme.colors.inkTertiary,
                        backgroundColor: theme.palette.background.default,
                        fontSize: 12,
                        lineHeight: 1.35,
                      })}
                    >
                      <AdvisoryEvidenceValue>{edge.relationship}</AdvisoryEvidenceValue>
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 500, minWidth: 0 }}>
                    <AdvisoryEvidenceText>{edge.to}</AdvisoryEvidenceText>
                  </Typography>
                  <Typography
                    sx={theme => ({
                      display: { xs: 'block', sm: 'none' },
                      gridColumn: '1 / -1',
                      color: theme.colors.inkTertiary,
                      fontSize: 12,
                    })}
                  >
                    <AdvisoryEvidenceValue>{edge.relationship}</AdvisoryEvidenceValue>
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

export function FinancialForecastRenderer({ section, compact }: SpecializedSectionProps) {
  const data = section.data as { basis?: string; rows?: ForecastRow[] };
  const rows = data.rows ?? [];
  const fields = section.fields as readonly string[];

  return (
    <Box
      data-testid="advisory-financial-forecast"
      sx={{ display: 'grid', gap: compact ? 2.5 : 3.5 }}
    >
      {fields.includes('basis') ? (
        <Box
          sx={theme => ({
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 1.5,
            paddingBottom: 2,
            borderBottom: `1px solid ${theme.visualPrimitives.border}`,
          })}
        >
          <Typography sx={theme => ({ ...labelSx, color: theme.colors.inkTertiary })}>
            Basis
          </Typography>
          <TextValue value={data.basis ?? ''} />
        </Box>
      ) : null}
      {rows.length === 0 ? (
        <KnownEmpty />
      ) : (
        <TableContainer
          role="region"
          aria-label={section.title}
          tabIndex={0}
          sx={theme => ({
            overflowX: 'auto',
            borderTop: `2px solid ${theme.colors.inkPrimary}`,
            '&:focus-visible': {
              outline: `${theme.visualPrimitives.focusRingWidth} solid ${theme.colors.lavenderDeep}`,
              outlineOffset: theme.visualPrimitives.focusRingOffset,
            },
          })}
        >
          <Table size="small" sx={{ minWidth: compact ? 480 : 600 }}>
            <TableHead>
              <TableRow>
                {['Line', 'Period', 'Value'].map((column, index) => (
                  <TableCell
                    key={column}
                    align={index === 2 ? 'right' : 'left'}
                    sx={theme => ({
                      ...labelSx,
                      color: theme.colors.inkTertiary,
                      borderBottom: `1px solid ${theme.visualPrimitives.border}`,
                      paddingX: index === 0 ? 0 : 2,
                      paddingY: 1.5,
                      whiteSpace: 'nowrap',
                    })}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow data-financial-row={`${row.line}|${row.period}`} key={index}>
                  <TableCell
                    sx={theme => ({
                      color: theme.colors.inkPrimary,
                      fontWeight: 500,
                      borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
                      paddingX: 0,
                      paddingY: compact ? 1.5 : 2,
                    })}
                  >
                    <AdvisoryEvidenceValue>{row.line}</AdvisoryEvidenceValue>
                  </TableCell>
                  <TableCell
                    sx={theme => ({
                      ...monoSx,
                      color: theme.colors.inkTertiary,
                      borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
                      paddingX: 2,
                    })}
                  >
                    <AdvisoryEvidenceValue>{row.period}</AdvisoryEvidenceValue>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={theme => ({
                      ...monoSx,
                      color: theme.colors.inkPrimary,
                      fontSize: compact ? 14 : 16,
                      fontWeight: 500,
                      borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
                      paddingX: 2,
                    })}
                  >
                    <AdvisoryEvidenceValue>{row.value}</AdvisoryEvidenceValue>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
